import React, { useState, useEffect, useRef } from 'react';
import { API_BASE, GOOGLE_CLIENT_ID } from '../config';

const TURNSTILE_SITE_KEY = '0x4AAAAAADLfFn8frPa_Pr7i';

function SignUpPage({ onBack, onLogin, onSuccess }) {
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [turnstileToken, setTurnstileToken] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(null);
  const turnstileRef = useRef(null);

  useEffect(() => {
    // Load Cloudflare Turnstile
    if (!document.querySelector('script[src*="challenges.cloudflare.com"]')) {
      const s = document.createElement('script');
      s.src = 'https://challenges.cloudflare.com/turnstile/v0/api.js';
      s.async = true; s.defer = true;
      document.head.appendChild(s);
    }

    // Load Google Sign-In
    if (GOOGLE_CLIENT_ID && !document.querySelector('script[src*="accounts.google.com/gsi/client"]')) {
      const s = document.createElement('script');
      s.src = 'https://accounts.google.com/gsi/client';
      s.async = true; s.defer = true;
      document.head.appendChild(s);
    }

    const checkAndRender = () => {
      if (window.turnstile && turnstileRef.current) {
        try {
          window.turnstile.render(turnstileRef.current, {
            sitekey: TURNSTILE_SITE_KEY,
            theme: 'dark',
            callback: (token) => setTurnstileToken(token),
            'error-callback': () => setError('Turnstile verification failed.'),
            'expired-callback': () => setTurnstileToken('')
          });
        } catch (e) {}
      } else {
        setTimeout(checkAndRender, 200);
      }

      // Render Google button
      if (window.google?.accounts?.id && GOOGLE_CLIENT_ID) {
        const googleBtn = document.getElementById('google-signin-btn');
        if (googleBtn && !googleBtn.dataset.rendered) {
          googleBtn.dataset.rendered = 'true';
          window.google.accounts.id.initialize({
            client_id: GOOGLE_CLIENT_ID,
            callback: handleGoogleSignIn
          });
          window.google.accounts.id.renderButton(googleBtn, {
            theme: 'filled_black', size: 'large', text: 'signup_with', shape: 'rectangular', width: 320
          });
        }
      }
    };
    checkAndRender();
  }, []);

  const handleGoogleSignIn = async (response) => {
    setLoading(true);
    setError('');
    try {
      const res = await fetch(`${API_BASE}/api/v3/auth/google`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ credential: response.credential })
      });
      const data = await res.json();
      if (!res.ok || !data.success) {
        setError(data.error || 'Google sign-in failed');
      } else {
        localStorage.setItem('astrogem_token', data.token);
        localStorage.setItem('astrogem_api_key', data.api_key);
        localStorage.setItem('astrogem_user', JSON.stringify(data.user));
        setSuccess(data);
      }
    } catch (e) {
      setError('Network error: ' + e.message);
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');

    // Validation
    if (!email || !password) {
      setError('Email and password required');
      return;
    }
    if (password.length < 8) {
      setError('Password must be at least 8 characters');
      return;
    }
    if (password !== confirmPassword) {
      setError('Passwords do not match');
      return;
    }
    if (!turnstileToken) {
      setError('Please complete the security check');
      return;
    }

    setLoading(true);
    try {
      const response = await fetch(`${API_BASE}/api/v3/auth/signup`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email, password, name, turnstileToken })
      });

      const data = await response.json();

      if (!response.ok || !data.success) {
        setError(data.error || 'Signup failed');
        if (window.turnstile) window.turnstile.reset();
        setTurnstileToken('');
      } else {
        // Save token + API key to localStorage
        localStorage.setItem('astrogem_token', data.token);
        localStorage.setItem('astrogem_api_key', data.api_key);
        localStorage.setItem('astrogem_user', JSON.stringify(data.user));
        setSuccess(data);
      }
    } catch (e) {
      setError('Network error: ' + e.message);
    } finally {
      setLoading(false);
    }
  };

  if (success) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-900 via-purple-950 to-slate-900 text-white flex items-center justify-center p-6">
        <div className="max-w-2xl w-full bg-slate-800/50 border border-slate-700 rounded-2xl p-8 text-center">
          <div className="text-6xl mb-4">🎉</div>
          <h1 className="text-3xl font-bold mb-2">Welcome to ASTROGEM API!</h1>
          <p className="text-white/70 mb-6">Your account is ready. Save your API key — it grants access to all 40+ endpoints.</p>

          <div className="bg-slate-900 border border-purple-500/50 rounded-lg p-4 text-left mb-6">
            <p className="text-xs text-white/50 mb-2">YOUR API KEY (save this securely):</p>
            <code className="block text-green-300 font-mono text-sm break-all">{success.api_key}</code>
            <button onClick={() => navigator.clipboard.writeText(success.api_key)}
              className="mt-3 px-3 py-1 bg-purple-500/20 hover:bg-purple-500/30 border border-purple-400 rounded text-xs">
              📋 Copy API Key
            </button>
          </div>

          <div className="bg-blue-500/10 border border-blue-500/30 rounded-lg p-4 text-left text-sm mb-6">
            <p className="font-semibold mb-2">Quick Start:</p>
            <pre className="text-xs text-blue-200 overflow-x-auto">{`curl -X POST https://astrogemapi.com/api/v3/vedic/birth-details \\
  -H "x-api-key: ${success.api_key.substring(0, 12)}..." \\
  -H "Content-Type: application/json" \\
  -d '{"subject":{"name":"You","birth_data":{...}}}'`}</pre>
          </div>

          <div className="flex gap-3 justify-center">
            <button onClick={onSuccess}
              className="px-6 py-3 bg-gradient-to-r from-purple-500 to-pink-500 hover:from-purple-600 hover:to-pink-600 rounded-lg font-bold">
              Go to Dashboard →
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-900 via-purple-950 to-slate-900 text-white flex items-center justify-center p-6">
      <div className="max-w-md w-full">
        <button onClick={onBack} className="text-white/60 hover:text-white text-sm mb-6">← Back</button>

        <div className="bg-slate-800/50 backdrop-blur border border-slate-700 rounded-2xl p-8">
          <div className="text-center mb-6">
            <div className="text-4xl mb-2">🪔</div>
            <h1 className="text-3xl font-bold mb-2">Create Account</h1>
            <p className="text-white/60 text-sm">100 free API calls/month. No credit card required.</p>
          </div>

          {/* Google Sign-In */}
          {GOOGLE_CLIENT_ID && (
            <>
              <div className="flex justify-center mb-4">
                <div id="google-signin-btn"></div>
              </div>
              <div className="flex items-center my-4">
                <div className="flex-1 border-t border-slate-700"></div>
                <span className="px-3 text-xs text-white/50">or continue with email</span>
                <div className="flex-1 border-t border-slate-700"></div>
              </div>
            </>
          )}

          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <label className="block text-xs font-semibold text-white/80 uppercase mb-2">Name (optional)</label>
              <input type="text" value={name} onChange={(e) => setName(e.target.value)}
                placeholder="Your Name"
                className="w-full px-4 py-3 bg-slate-900 border border-slate-700 rounded-lg focus:outline-none focus:border-purple-400" />
            </div>

            <div>
              <label className="block text-xs font-semibold text-white/80 uppercase mb-2">Email *</label>
              <input type="email" value={email} onChange={(e) => setEmail(e.target.value)} required
                placeholder="you@example.com"
                className="w-full px-4 py-3 bg-slate-900 border border-slate-700 rounded-lg focus:outline-none focus:border-purple-400" />
            </div>

            <div>
              <label className="block text-xs font-semibold text-white/80 uppercase mb-2">Password * (min 8 chars)</label>
              <input type="password" value={password} onChange={(e) => setPassword(e.target.value)} required minLength={8}
                placeholder="••••••••"
                className="w-full px-4 py-3 bg-slate-900 border border-slate-700 rounded-lg focus:outline-none focus:border-purple-400" />
            </div>

            <div>
              <label className="block text-xs font-semibold text-white/80 uppercase mb-2">Confirm Password *</label>
              <input type="password" value={confirmPassword} onChange={(e) => setConfirmPassword(e.target.value)} required
                placeholder="••••••••"
                className="w-full px-4 py-3 bg-slate-900 border border-slate-700 rounded-lg focus:outline-none focus:border-purple-400" />
            </div>

            {/* Cloudflare Turnstile Widget */}
            <div className="flex justify-center py-2">
              <div ref={turnstileRef}></div>
            </div>

            {error && (
              <div className="bg-red-500/10 border border-red-500/40 rounded-lg p-3 text-sm text-red-300">
                ⚠️ {error}
              </div>
            )}

            <button type="submit" disabled={loading || !turnstileToken}
              className="w-full px-6 py-3 bg-gradient-to-r from-purple-500 to-pink-500 hover:from-purple-600 hover:to-pink-600 disabled:opacity-50 disabled:cursor-not-allowed rounded-lg font-bold transition">
              {loading ? '⏳ Creating account...' : '🪔 Create Account'}
            </button>

            <p className="text-center text-sm text-white/60">
              Already have an account?{' '}
              <button type="button" onClick={onLogin} className="text-purple-300 hover:text-purple-200 font-semibold">
                Sign In
              </button>
            </p>
          </form>
        </div>

        <p className="text-center text-xs text-white/40 mt-6">
          🛡️ Protected by Cloudflare Turnstile • Your data is secure
        </p>
      </div>
    </div>
  );
}

export default SignUpPage;
